import { uploadMedia } from "./mediaUpload";
// ══════════════════════════════════════════════════════════════════════════════
// PriestPortalTab.js — In-app priest portal: register → pending → dashboard
// Used inside PujaPage as the "Priest Portal" tab
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";
import PujaRoom from "./PujaRoom";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const C = {
  bg2: "#0D0818", bg3: "#130D22",
  saffron: "#E8832A", gold: "#C9953A", cream: "#FFF5E0",
  text: "#EDE0C8", dim: "#8A7558",
  border: "rgba(201,149,58,0.25)",
  glow: "rgba(232,131,42,0.10)",
};

const SPECIALIZATIONS = [
  "Ganesh Puja", "Satyanarayan Puja", "Rudrabhishek", "Navagraha Puja",
  "Griha Pravesh", "Vivah / Wedding", "Namkaran", "Annaprashan",
  "Sundara Kanda Path", "Hanuman Puja", "Durga Puja", "Lakshmi Puja",
  "Pitru Tarpan", "Mrityunjaya Japa", "Homa / Havan",
];
const LANGUAGES = ["Telugu", "Hindi", "Sanskrit", "Tamil", "Kannada", "Malayalam", "Marathi", "English"];

const STATUS_COLORS = {
  pending:   { bg: "rgba(250,204,21,0.08)",  border: "rgba(250,204,21,0.3)",  text: "#fde68a" },
  confirmed: { bg: "rgba(52,211,153,0.08)",  border: "rgba(52,211,153,0.3)",  text: "#6ee7b7" },
  completed: { bg: "rgba(139,92,246,0.08)",  border: "rgba(139,92,246,0.3)",  text: "#c4b5fd" },
  cancelled: { bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.25)",  text: "#fca5a5" },
};

// ── Main export ───────────────────────────────────────────────────────────────

export default function PriestPortalTab({ user }) {
  const [screen, setScreen] = useState("loading"); // loading | register | pending | rejected | dashboard
  const [priest, setPriest] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loadErr, setLoadErr] = useState("");

  // Load priest profile on mount
  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API}/priest/by-user/${user.id}`)
      .then(r => r.status === 404 ? null : r.json())
      .then(data => {
        if (!data) { setScreen("register"); return; }
        if (data.detail) { setScreen("register"); return; }
        setPriest(data);
        if (data.status === "approved") {
          // Also fetch assigned bookings
          // Authorized by the priest's own login token (added by authFetch).
          fetch(`${API}/priest/${data.id}/bookings`)
            .then(r => r.json())
            .then(b => { setBookings(Array.isArray(b) ? b : []); setScreen("dashboard"); })
            .catch(() => setScreen("dashboard"));
        } else if (data.status === "rejected") {
          setScreen("rejected");
        } else {
          setScreen("pending");
        }
      })
      .catch(() => setScreen("register"));
  }, [user?.id]);

  if (screen === "loading") return <Loading />;

  if (screen === "register") return (
    <RegisterForm
      user={user}
      onSuccess={data => { setPriest(data); setScreen("pending"); }}
    />
  );

  if (screen === "pending") return <PendingScreen priest={priest} />;

  if (screen === "rejected") return <RejectedScreen priest={priest} />;

  if (screen === "dashboard") return (
    <Dashboard
      priest={priest}
      bookings={bookings}
      user={user}
    />
  );

  return null;
}

// ── Registration form ─────────────────────────────────────────────────────────

function RegisterForm({ user, onSuccess }) {
  const [form, setForm] = useState({
    name: user?.user_metadata?.full_name || "",
    phone: "",
    email: user?.email || "",
    bio: "",
    specializations: [],
    languages: [],
    experience_years: "",
    location: "",
    photo_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm(f => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim())  return setErr("Please enter your full name.");
    if (!form.phone.trim()) return setErr("Please enter your WhatsApp number.");
    if (!form.specializations.length) return setErr("Select at least one specialization.");
    if (!form.languages.length)       return setErr("Select at least one language.");

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/priest/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          experience_years: parseInt(form.experience_years, 10) || 0,
          priest_user_id: user?.id || "",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      onSuccess({ ...form, id: data.id, status: "pending", priest_user_id: user?.id });
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: "600px" }}>
      {/* Header */}
      <div style={{ marginBottom: "28px" }}>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.3rem", color: C.saffron, marginBottom: "6px" }}>
          🔱 Join as a Priest
        </div>
        <div style={{ fontSize: "0.9rem", color: C.dim, lineHeight: "1.6" }}>
          Submit your application to conduct online pujas for VedSanga devotees.
          Our team reviews every application and will contact you on WhatsApp.
        </div>
      </div>

      <form onSubmit={submit}>
        {/* Photo upload — top of form */}
        <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "20px" }}>
          <PhotoUpload
            userId={user?.id}
            currentUrl={form.photo_url}
            onUpload={url => set("photo_url", url)}
            size={80}
          />
          <div>
            <div style={{ fontSize: "0.88rem", color: C.text, marginBottom: "4px" }}>Profile Photo</div>
            <div style={{ fontSize: "0.78rem", color: C.dim, lineHeight: "1.6" }}>
              A clear face photo builds trust with devotees.<br />
              JPG or PNG, max 2MB.
            </div>
          </div>
        </div>
        <Card title="Personal Details">
          <Row>
            <Field label="Full Name *">
              <Input placeholder="Your full name" value={form.name} onChange={v => set("name", v)} />
            </Field>
            <Field label="WhatsApp Number *">
              <Input placeholder="+91 99999 99999" value={form.phone} onChange={v => set("phone", v)} />
            </Field>
          </Row>
          <Row>
            <Field label="Years of Experience *">
              <Input type="number" placeholder="e.g. 10" value={form.experience_years} onChange={v => set("experience_years", v)} />
            </Field>
            <Field label="Location">
              <Input placeholder="City, State" value={form.location} onChange={v => set("location", v)} />
            </Field>
          </Row>
          <Field label="About You">
            <textarea
              value={form.bio}
              onChange={e => set("bio", e.target.value)}
              placeholder="Your lineage, training, experience..."
              style={{ ...inputSt, minHeight: "70px", resize: "vertical", lineHeight: "1.6" }}
            />
          </Field>
        </Card>

        <Card title="Specializations *">
          <div style={{ fontSize: "0.8rem", color: C.dim, marginBottom: "10px" }}>
            Select all puja types you can perform
          </div>
          <Pills items={SPECIALIZATIONS} selected={form.specializations} onToggle={v => toggle("specializations", v)} />
        </Card>

        <Card title="Languages *">
          <div style={{ fontSize: "0.8rem", color: C.dim, marginBottom: "10px" }}>
            Languages you can conduct puja in
          </div>
          <Pills items={LANGUAGES} selected={form.languages} onToggle={v => toggle("languages", v)} />
        </Card>

        {err && <ErrBox msg={err} />}

        <button type="submit" disabled={submitting} style={submitBtnStyle(submitting)}>
          {submitting ? "Submitting…" : "Submit Application"}
        </button>
      </form>
    </div>
  );
}

// ── Pending screen ────────────────────────────────────────────────────────────

function PendingScreen({ priest }) {
  return (
    <div style={{ maxWidth: "520px" }}>
      <div style={{
        background: "rgba(250,204,21,0.06)", border: "1px solid rgba(250,204,21,0.25)",
        borderRadius: "16px", padding: "32px", textAlign: "center",
      }}>
        <div style={{ fontSize: "3rem", marginBottom: "16px" }}>⏳</div>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.1rem", color: "#fde68a", marginBottom: "12px" }}>
          Application Under Review
        </div>
        <div style={{ color: C.text, lineHeight: "1.8", fontSize: "0.95rem" }}>
          Thank you, <strong style={{ color: C.cream }}>{priest?.name}</strong>. Your application has been submitted.
          <br /><br />
          Our team will review your profile and contact you on WhatsApp
          (<span style={{ color: C.gold }}>{priest?.phone}</span>) within 2–3 business days.
        </div>
      </div>
      <div style={{
        marginTop: "20px", background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "12px", padding: "18px 22px",
        fontSize: "0.82rem", color: C.dim, lineHeight: "1.8",
      }}>
        <div style={{ color: C.saffron, fontFamily: "'Cinzel Decorative', serif", fontSize: "0.72rem", marginBottom: "8px", letterSpacing: "0.05em" }}>
          WHILE YOU WAIT — PREPARE
        </div>
        ✓ Place your camera 3–4 feet away at a 45° angle to your side<br />
        ✓ Frame your face, hands, and deity idol in one shot<br />
        ✓ Use warm light (lamp/diya) in front — no backlight behind you<br />
        ✓ Stable internet (5+ Mbps upload) and microphone tested<br />
        ✓ Keep WhatsApp active — we'll confirm your first booking there
      </div>
    </div>
  );
}

// ── Rejected screen ───────────────────────────────────────────────────────────

function RejectedScreen({ priest }) {
  return (
    <div style={{ maxWidth: "480px" }}>
      <div style={{
        background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)",
        borderRadius: "16px", padding: "32px", textAlign: "center",
      }}>
        <div style={{ fontSize: "2.5rem", marginBottom: "14px" }}>🙏</div>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1rem", color: "#fca5a5", marginBottom: "12px" }}>
          Application Not Approved
        </div>
        <div style={{ color: C.text, lineHeight: "1.8", fontSize: "0.9rem" }}>
          Thank you for your interest, {priest?.name}. Unfortunately we were unable to approve
          your application at this time.
          {priest?.admin_notes && (
            <div style={{ marginTop: "14px", padding: "12px 16px", background: "rgba(255,255,255,0.04)", borderRadius: "8px", fontSize: "0.85rem", color: C.dim }}>
              {priest.admin_notes}
            </div>
          )}
          <br />
          Please contact us on WhatsApp if you have any questions.
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ priest: initialPriest, bookings, user }) {
  const [tab, setTab] = useState("upcoming");
  const [roomData, setRoomData] = useState(null);
  const [joiningId, setJoiningId] = useState(null);
  const [priest, setPriest] = useState(initialPriest);

  const handlePhotoUpdate = async (url) => {
    try {
      await fetch(`${API}/priest/${priest.id}/photo`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photo_url: url }),
      });
      setPriest(p => ({ ...p, photo_url: url }));
    } catch (e) {
      // photo saved in storage, just couldn't update DB — silent fail
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const upcoming = bookings.filter(b => b.status !== "cancelled" && b.status !== "completed" && b.preferred_date >= today);
  const past     = bookings.filter(b => b.status === "completed" || b.preferred_date < today);
  const earnings = bookings.filter(b => b.status === "completed").reduce((s, b) => s + (b.price || 0), 0);

  const handleJoin = async (b) => {
    setJoiningId(b.id);
    try {
      const res  = await fetch(`${API}/puja/priest-token/${b.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to get token");
      setRoomData({
        config: { appId: data.app_id, token: data.token, channel: data.channel, uid: data.uid, priestUid: data.uid, priestName: priest?.name || "Priest" },
        booking: b,
      });
    } catch (e) {
      alert("Could not join: " + (e.message || "Unknown error"));
    } finally {
      setJoiningId(null);
    }
  };

  if (roomData) {
    return (
      <PujaRoom
        booking={{ puja_name: roomData.booking.puja_name, puja_emoji: "🔱" }}
        agoraConfig={roomData.config}
        onLeave={() => setRoomData(null)}
      />
    );
  }

  const shown = tab === "upcoming" ? upcoming : past;

  return (
    <div style={{ maxWidth: "700px" }}>
      {/* Priest header */}
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "16px", padding: "22px 26px", marginBottom: "24px",
        display: "flex", alignItems: "center", gap: "20px",
      }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <PhotoUpload
            userId={user?.id}
            currentUrl={priest?.photo_url}
            onUpload={handlePhotoUpdate}
            size={68}
            name={priest?.name}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.05rem", color: C.cream }}>
            {priest?.name}
          </div>
          <div style={{ fontSize: "0.8rem", color: C.dim, marginTop: "3px" }}>
            {(priest?.languages || []).join(" · ")}{priest?.location ? ` · ${priest.location}` : ""}
          </div>
          <div style={{ fontSize: "0.72rem", color: C.dim, marginTop: "4px", fontStyle: "italic" }}>
            {priest?.experience_years ? `${priest.experience_years} yrs experience` : ""}
            {priest?.experience_years && priest?.bio ? " · " : ""}
            {priest?.bio ? priest.bio.slice(0, 60) + (priest.bio.length > 60 ? "…" : "") : ""}
          </div>
        </div>
        <span style={{
          background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)",
          color: "#6ee7b7", borderRadius: "20px",
          padding: "3px 12px", fontSize: "0.72rem", letterSpacing: "0.05em",
          flexShrink: 0,
        }}>
          APPROVED
        </span>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px", marginBottom: "24px" }}>
        {[
          ["Upcoming", upcoming.length, C.saffron],
          ["Completed", past.filter(b => b.status === "completed").length, "#6ee7b7"],
          ["Earnings", `₹${earnings.toLocaleString("en-IN")}`, C.gold],
        ].map(([lbl, val, clr]) => (
          <div key={lbl} style={{
            background: C.bg2, border: `1px solid ${C.border}`,
            borderRadius: "12px", padding: "14px", textAlign: "center",
          }}>
            <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.4rem", color: clr }}>{val}</div>
            <div style={{ fontSize: "0.75rem", color: C.dim, marginTop: "3px" }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: "18px" }}>
        {[["upcoming", `Upcoming (${upcoming.length})`], ["past", `Past (${past.length})`]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            background: "none", border: "none",
            borderBottom: tab === id ? `2px solid ${C.saffron}` : "2px solid transparent",
            color: tab === id ? C.saffron : C.dim,
            fontFamily: "'Crimson Pro', serif", fontSize: "0.95rem",
            padding: "8px 18px", cursor: "pointer", marginBottom: "-1px",
          }}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Booking list */}
      {shown.length === 0 ? (
        <div style={{ color: C.dim, padding: "36px 0", textAlign: "center", fontStyle: "italic" }}>
          No {tab} bookings
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {shown.map(b => (
            <BookingRow key={b.id} b={b} onJoin={() => handleJoin(b)} joining={joiningId === b.id} />
          ))}
        </div>
      )}

      {/* Specializations */}
      {priest?.specializations?.length > 0 && (
        <div style={{
          background: C.bg2, border: `1px solid ${C.border}`,
          borderRadius: "12px", padding: "18px 22px", marginTop: "24px",
        }}>
          <div style={{ fontSize: "0.72rem", color: C.saffron, fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.05em", marginBottom: "12px" }}>
            YOUR SPECIALIZATIONS
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {priest.specializations.map(s => (
              <span key={s} style={{
                background: C.glow, border: `1px solid ${C.border}`,
                borderRadius: "20px", padding: "3px 12px",
                fontSize: "0.8rem", color: C.text,
              }}>{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BookingRow({ b, onJoin, joining }) {
  const [expanded, setExpanded] = useState(false);
  const today     = new Date().toISOString().split("T")[0];
  const isToday   = b.preferred_date === today;
  const joinable  = b.status === "confirmed";
  const sc        = STATUS_COLORS[b.status] || STATUS_COLORS.pending;

  return (
    <div style={{
      background: C.bg2,
      border: `1px solid ${isToday ? C.saffron : C.border}`,
      borderRadius: "12px", padding: "16px 20px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px", flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.88rem", color: C.cream }}>
              {b.puja_name}
            </span>
            <span style={{
              background: sc.bg, border: `1px solid ${sc.border}`,
              color: sc.text, borderRadius: "20px",
              padding: "1px 8px", fontSize: "0.68rem", letterSpacing: "0.05em",
            }}>{b.status}</span>
            {isToday && (
              <span style={{
                background: "rgba(232,131,42,0.15)", border: `1px solid ${C.saffron}`,
                color: C.saffron, borderRadius: "20px",
                padding: "1px 8px", fontSize: "0.68rem",
              }}>TODAY</span>
            )}
          </div>
          <div style={{ fontSize: "0.8rem", color: C.dim }}>
            {b.preferred_date} · {b.preferred_time} IST &nbsp;·&nbsp; {b.booking_type === "family" ? "Family" : "Individual"}
          </div>
          <div style={{ fontSize: "0.8rem", color: C.dim, marginTop: "3px" }}>
            Devotee: <span style={{ color: C.text }}>{b.name || "—"}</span>
            &nbsp;·&nbsp; ₹{(b.price || 0).toLocaleString("en-IN")}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", marginLeft: "12px", flexShrink: 0 }}>
          {joinable && (
            <button onClick={onJoin} disabled={joining} style={{
              background: joining ? "rgba(232,131,42,0.3)" : "linear-gradient(135deg,#E8832A,#C9953A)",
              border: "none", borderRadius: "8px",
              padding: "7px 14px", color: "white",
              fontFamily: "'Crimson Pro', serif", fontSize: "0.85rem",
              cursor: joining ? "wait" : "pointer",
            }}>
              {joining ? "…" : "Join Puja"}
            </button>
          )}
          {joinable && (
            <a
              href={`/?priest_monitor=${b.id}&secret=${encodeURIComponent(b.link_sig || "")}`}
              target="_blank"
              rel="noreferrer"
              title="Open devotee monitor on a second device (tablet/laptop)"
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: "8px", padding: "7px 12px",
                color: C.dim, textDecoration: "none",
                fontSize: "0.78rem", cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              📺 Monitor
            </a>
          )}
          <button onClick={() => setExpanded(x => !x)} style={{
            background: "transparent", border: `1px solid ${C.border}`,
            borderRadius: "8px", padding: "7px 10px",
            color: C.dim, cursor: "pointer", fontSize: "0.8rem",
          }}>{expanded ? "▲" : "▼"}</button>
        </div>
      </div>
      {expanded && (
        <div style={{
          marginTop: "12px", paddingTop: "12px",
          borderTop: "1px solid rgba(201,149,58,0.1)",
          fontSize: "0.8rem", color: C.dim,
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px",
        }}>
          {[
            ["Booking ID", b.id?.slice(0,8) + "…"],
            b.gotra    && ["Gotra",    b.gotra],
            b.occasion && ["Occasion", b.occasion],
            b.sankalpa && ["Sankalpa", b.sankalpa],
            b.members  && ["Members",  b.members],
          ].filter(Boolean).map(([k, v]) => (
            <div key={k}><span style={{ color: C.dim }}>{k}: </span><span style={{ color: C.text }}>{v}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Photo upload ──────────────────────────────────────────────────────────────

function PhotoUpload({ userId, currentUrl, onUpload, size = 72, name = "" }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || "");

  // Sync if parent updates photo_url (e.g. after DB save)
  useEffect(() => { setPreview(currentUrl || ""); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Photo must be under 2MB"); return; }

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);

    try {
      const publicUrl = await uploadMedia("priest-photos", file, file.name);

      setPreview(publicUrl);
      onUpload(publicUrl);
    } catch (err) {
      alert("Upload failed: " + (err.message || "Unknown error"));
      setPreview(currentUrl || "");
    } finally {
      setUploading(false);
    }
  };

  const initials = name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "🔱";

  return (
    <div
      style={{ position: "relative", width: size, height: size, cursor: "pointer", flexShrink: 0 }}
      onClick={() => !uploading && fileRef.current?.click()}
      title="Click to change photo"
    >
      {/* Avatar circle */}
      {preview ? (
        <img
          src={preview}
          alt={name}
          style={{
            width: size, height: size, borderRadius: "50%",
            objectFit: "cover",
            border: `2px solid ${C.border}`,
            display: "block",
          }}
        />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: "50%",
          background: "linear-gradient(135deg, #E8832A, #C9953A)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: size > 60 ? "1.4rem" : "1rem",
          color: "white", fontWeight: "700",
          border: `2px solid ${C.border}`,
          fontFamily: "'Cinzel Decorative', serif",
        }}>
          {initials}
        </div>
      )}

      {/* Upload overlay */}
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: uploading ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.2s",
        opacity: uploading ? 1 : 0,
      }}>
        {uploading && <div style={{ color: "white", fontSize: "0.7rem" }}>…</div>}
      </div>

      {/* Camera icon badge */}
      {!uploading && (
        <div style={{
          position: "absolute", bottom: 0, right: 0,
          width: size > 60 ? 24 : 18, height: size > 60 ? 24 : 18,
          background: C.saffron, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: size > 60 ? "0.7rem" : "0.55rem",
          border: "2px solid #06030A",
        }}>
          📷
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
}

// ── Shared UI helpers ─────────────────────────────────────────────────────────

function Loading() {
  return (
    <div style={{ textAlign: "center", padding: "48px 0" }}>
      <div style={{ fontSize: "2.5rem", color: C.saffron }}>ॐ</div>
      <div style={{ color: C.dim, fontSize: "0.85rem", marginTop: "10px", fontStyle: "italic" }}>Loading…</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{
      background: C.bg2, border: `1px solid ${C.border}`,
      borderRadius: "14px", padding: "20px 24px", marginBottom: "16px",
    }}>
      <div style={{
        fontFamily: "'Cinzel Decorative', serif", fontSize: "0.72rem",
        color: C.saffron, letterSpacing: "0.06em",
        marginBottom: "16px", paddingBottom: "10px",
        borderBottom: "1px solid rgba(201,149,58,0.1)",
      }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontSize: "0.75rem", color: C.dim, marginBottom: "5px" }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ type = "text", placeholder, value, onChange }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={inputSt}
    />
  );
}

function Pills({ items, selected, onToggle }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
      {items.map(item => {
        const on = selected.includes(item);
        return (
          <button key={item} type="button" onClick={() => onToggle(item)} style={{
            padding: "5px 12px", borderRadius: "20px", fontSize: "0.8rem",
            border: on ? `1px solid ${C.saffron}` : `1px solid ${C.border}`,
            background: on ? "rgba(232,131,42,0.12)" : "transparent",
            color: on ? C.saffron : C.dim,
            cursor: "pointer", transition: "all 0.15s",
          }}>
            {on ? "✓ " : ""}{item}
          </button>
        );
      })}
    </div>
  );
}

function ErrBox({ msg }) {
  return (
    <div style={{
      background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
      borderRadius: "8px", padding: "10px 14px", marginBottom: "14px",
      fontSize: "0.83rem", color: "#fca5a5",
    }}>
      {msg}
    </div>
  );
}

const inputSt = {
  width: "100%", background: "#06030A",
  border: "1px solid rgba(201,149,58,0.25)",
  borderRadius: "8px", padding: "9px 12px",
  color: "#EDE0C8", fontFamily: "'Crimson Pro', serif",
  fontSize: "0.92rem", outline: "none",
};

function submitBtnStyle(disabled) {
  return {
    width: "100%", padding: "13px",
    background: disabled ? "rgba(232,131,42,0.3)" : "linear-gradient(135deg,#E8832A,#C9953A)",
    border: "none", borderRadius: "10px", color: "white",
    fontFamily: "'Cinzel Decorative', serif",
    fontSize: "0.9rem", letterSpacing: "0.04em",
    cursor: disabled ? "wait" : "pointer",
    boxShadow: disabled ? "none" : "0 4px 18px rgba(232,131,42,0.25)",
    transition: "all 0.2s",
  };
}
